import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Coins, LogOut, Library, Store, ScrollText, Wallet, ShieldCheck, KeyRound, CreditCard } from "lucide-react";
import Logo from "./Logo";

function CoinPill({ coins }) {
    const negative = coins < 0;
    return (
        <div
            data-testid="wallet-coin-pill"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold text-sm border shadow-sm ${
                negative
                    ? "bg-red-100 text-red-800 border-red-200"
                    : "bg-zapp/10 text-zapp-dark border-zapp/30"
            }`}
        >
            <Coins className="h-4 w-4" />
            <span>{coins} Zapp</span>
        </div>
    );
}

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    if (!user) return null;

    const isAdmin = user.role === "admin";

    return (
        <header className="sticky top-0 z-40 bg-paper/95 backdrop-blur border-b border-parchment">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
                <Link to={isAdmin ? "/admin" : "/app/storefront"} className="flex items-center gap-3" data-testid="nav-logo">
                    <Logo height={78} />
                    <div className="hidden sm:block text-[10px] uppercase tracking-widest text-ink/50 font-bold border-l border-parchment pl-3">
                        {isAdmin ? "Admin" : "Student"}
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    {isAdmin ? (
                        <>
                            <NavLink to="/admin" end className={({isActive}) => `btn-ghost ${isActive ? "text-brick" : ""}`} data-testid="nav-admin-schools">
                                <Library className="h-4 w-4" /> Schools
                            </NavLink>
                            <NavLink to="/admin/password" className={({isActive}) => `btn-ghost ${isActive ? "text-brick" : ""}`} data-testid="nav-admin-password">
                                <KeyRound className="h-4 w-4" /> Password
                            </NavLink>
                        </>
                    ) : (
                        <>
                            <NavLink to="/app/storefront" className={({isActive}) => `btn-ghost ${isActive ? "text-brick" : ""}`} data-testid="nav-storefront">
                                <Store className="h-4 w-4" /> Storefront
                            </NavLink>
                            <NavLink to="/app/my-books" className={({isActive}) => `btn-ghost ${isActive ? "text-brick" : ""}`} data-testid="nav-my-books">
                                <Library className="h-4 w-4" /> My Books
                            </NavLink>
                            <NavLink to="/app/reservations" className={({isActive}) => `btn-ghost ${isActive ? "text-brick" : ""}`} data-testid="nav-reservations">
                                <ScrollText className="h-4 w-4" /> Reservations
                            </NavLink>
                            <NavLink to="/app/wallet" className={({isActive}) => `btn-ghost ${isActive ? "text-brick" : ""}`} data-testid="nav-wallet">
                                <Wallet className="h-4 w-4" /> Wallet
                            </NavLink>
                            <NavLink to="/app/subscription" className={({isActive}) => `btn-ghost ${isActive ? "text-brick" : ""}`} data-testid="nav-subscription">
                                <CreditCard className="h-4 w-4" /> Membership
                            </NavLink>
                        </>
                    )}
                </nav>

                <div className="flex items-center gap-3">
                    {!isAdmin && <CoinPill coins={user.zapp_coins ?? 0} />}
                    {isAdmin && (
                        <div className="hidden sm:flex items-center gap-1.5 text-xs uppercase tracking-widest font-semibold text-ink/60">
                            <ShieldCheck className="h-4 w-4 text-library" /> {user.email}
                        </div>
                    )}
                    <button data-testid="nav-logout-btn" onClick={handleLogout} className="btn-ghost" title="Logout">
                        <LogOut className="h-4 w-4" />
                    </button>
                </div>
            </div>
            {/* mobile nav */}
            <div className="md:hidden border-t border-parchment px-4 py-2 flex items-center gap-2 overflow-x-auto text-sm">
                {isAdmin ? (
                    <>
                        <NavLink to="/admin" end className={({isActive}) => `btn-ghost text-sm ${isActive ? "text-brick" : ""}`}>Schools</NavLink>
                        <NavLink to="/admin/password" className={({isActive}) => `btn-ghost text-sm ${isActive ? "text-brick" : ""}`}>Password</NavLink>
                    </>
                ) : (
                    <>
                        <NavLink to="/app/storefront" className={({isActive}) => `btn-ghost text-sm ${isActive ? "text-brick" : ""}`}>Storefront</NavLink>
                        <NavLink to="/app/my-books" className={({isActive}) => `btn-ghost text-sm ${isActive ? "text-brick" : ""}`}>Books</NavLink>
                        <NavLink to="/app/reservations" className={({isActive}) => `btn-ghost text-sm ${isActive ? "text-brick" : ""}`}>Reservations</NavLink>
                        <NavLink to="/app/wallet" className={({isActive}) => `btn-ghost text-sm ${isActive ? "text-brick" : ""}`}>Wallet</NavLink>
                        <NavLink to="/app/subscription" className={({isActive}) => `btn-ghost text-sm ${isActive ? "text-brick" : ""}`}>Membership</NavLink>
                    </>
                )}
            </div>
        </header>
    );
}
